import { useMemo } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Landmark, Building2, Layers, GitBranch } from 'lucide-react'
import './FundDetail.css'
import {
  PageHeader, SectionCard, Badge, EmptyState, Button, HealthBadge,
  DescriptionList, type DLRow,
  DataTable, type Column,
  AssociationCard, AssocRow,
} from '@/components/ui'
import { RecordHero } from '@/components/workspace'
import { useStore } from '@/store/useStore'
import {
  accountById, trackById, propertiesForFund, latestRevenue, latestOccupancy,
} from './fundUtils'
import { money, num, pct } from '@/lib/format'
import type { Property, PropertyState } from '@/types/entities'

const STATE_TONE: Record<PropertyState, 'ok' | 'warn' | 'alert' | 'neutral' | 'info' | 'navy'> = {
  Owned: 'navy',
  Managed: 'info',
  'In Development': 'warn',
  'Under Contract': 'warn',
  Target: 'neutral',
  Disposition: 'alert',
  Offboarded: 'neutral',
}

function occColor(occ?: number): string {
  if (occ == null) return 'var(--wpi-platinum)'
  if (occ >= 90) return 'var(--ok)'
  if (occ >= 82) return 'var(--warn)'
  return 'var(--alert)'
}

export default function FundDetail() {
  const { id } = useParams()
  const data = useStore((s) => s.data)
  const navigate = useNavigate()
  const fund = useMemo(() => data.funds.find((f) => f.id === id), [data, id])

  if (!fund) {
    return (
      <>
        <PageHeader eyebrow="Fund" title={<>Fund <b>Not Found</b></>} />
        <SectionCard pad>
          <EmptyState
            icon={<Landmark size={36} />}
            title="Fund not found"
            hint={<>No vehicle matches <code>{id}</code>.</>}
            action={<Link to="/funds"><Button variant="primary">Back to Funds</Button></Link>}
          />
        </SectionCard>
      </>
    )
  }

  const investor = accountById(data, fund.investorAccountIds[0])
  const track = trackById(data, fund.trackId)
  const props = propertiesForFund(data, fund.id)

  /* ---- portfolio rollup ---- */
  const annualRev = props.reduce((s, p) => s + latestRevenue(p), 0) * 12
  const occVals = props.map((p) => latestOccupancy(p)).filter((v): v is number => v != null)
  const wtSqft = props.reduce((s, p) => s + (p.nrsf ?? 0), 0)
  // Weighted occupancy: only properties with an actual occupancy snapshot contribute
  // their NRSF to the denominator, so unmeasured assets don't dilute the average.
  let occWeightedSum = 0
  let occWeight = 0
  for (const p of props) {
    const occ = latestOccupancy(p)
    if (occ == null) continue
    const sf = p.nrsf ?? 0
    occWeightedSum += occ * sf
    occWeight += sf
  }
  const wtOcc = occWeight > 0
    ? occWeightedSum / occWeight
    : (occVals.length ? occVals.reduce((s, v) => s + v, 0) / occVals.length : undefined)
  const totalUnits = props.reduce((s, p) => s + (p.totalUnits ?? 0), 0)

  const keyRows: DLRow[] = [
    { label: 'Vintage', value: fund.vintage ?? '—' },
    {
      label: 'Investor Account',
      value: investor ? <Link to={`/accounts/${investor.id}`}>{investor.name}</Link> : '—',
    },
    { label: 'Target Size', value: fund.targetSize != null ? money(fund.targetSize, { compact: true }) : '—' },
    { label: 'Committed', value: fund.committedCapital != null ? money(fund.committedCapital, { compact: true }) : '—' },
    {
      label: 'Relationship Track',
      value: track ? <Link to={`/accounts/${track.accountId}/deals`}>{track.name}</Link> : '—',
    },
    { label: 'Properties', value: num(props.length) },
  ]

  const heroKpis = [
    { label: 'Properties', value: num(props.length) },
    { label: 'In-Place Rev', value: annualRev > 0 ? money(annualRev, { compact: true }) : '—', unit: 'yr' },
    { label: 'Wtd. Occupancy', value: wtOcc != null ? wtOcc.toFixed(1) : '—', unit: '%' },
    { label: 'Total Units', value: totalUnits > 0 ? num(totalUnits) : '—' },
  ]

  const cols: Column<Property>[] = [
    {
      key: 'name',
      header: 'Property',
      sortValue: (p) => p.name,
      render: (p) => (
        <Link to={`/properties/${p.id}`} onClick={(e) => e.stopPropagation()} style={{ fontWeight: 600 }}>
          {p.name}
        </Link>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      sortValue: (p) => `${p.address?.state ?? ''}${p.address?.city ?? ''}`,
      render: (p) => p.address?.city
        ? <span className="muted">{p.address.city}, {p.address.state}</span>
        : <span className="dim">—</span>,
    },
    {
      key: 'state',
      header: 'Status',
      sortValue: (p) => p.state,
      render: (p) => <Badge tone={STATE_TONE[p.state]} variant="soft">{p.state}</Badge>,
    },
    {
      key: 'occ',
      header: 'Occupancy',
      align: 'right',
      sortValue: (p) => latestOccupancy(p) ?? -1,
      render: (p) => {
        const occ = latestOccupancy(p)
        return (
          <span className="fund-occ">
            <span className="fund-occ-track">
              <span className="fund-occ-fill" style={{ width: `${Math.min(occ ?? 0, 100)}%`, background: occColor(occ) }} />
            </span>
            <span className="num">{occ != null ? pct(occ) : '—'}</span>
          </span>
        )
      },
    },
    {
      key: 'rev',
      header: 'Monthly Revenue',
      align: 'right',
      sortValue: (p) => latestRevenue(p),
      render: (p) => {
        const r = latestRevenue(p)
        return <span className="num">{r > 0 ? money(r) : '—'}</span>
      },
    },
    {
      key: 'health',
      header: 'Health',
      align: 'center',
      sortValue: (p) => p.health ?? '',
      render: (p) => <HealthBadge health={p.health} />,
    },
  ]

  const showCapitalBar = fund.committedCapital != null && fund.targetSize != null && fund.targetSize > 0
  const committedPct = showCapitalBar ? (fund.committedCapital! / fund.targetSize!) * 100 : 0

  return (
    <>
      <RecordHero
        eyebrow="Fund / Vehicle"
        title={fund.name}
        breadcrumb={<><Link to="/funds">Funds</Link> <span className="dim">/</span> {fund.vintage ?? 'Vehicle'}</>}
        badges={
          <>
            {fund.vintage && <Badge tone="info" variant="soft">Vintage {fund.vintage}</Badge>}
            {investor && <Badge tone="navy" variant="outline">{investor.name}</Badge>}
          </>
        }
        kpis={heroKpis}
      />

      <div className="fund-layout">
        {/* ---------- MAIN ---------- */}
        <div className="fund-main">
          <SectionCard title={<>Key <b>Information</b></>} eyebrow="Vehicle record">
            {fund.strategy && (
              <p className="muted" style={{ lineHeight: 1.6, marginBottom: 'var(--sp-4)' }}>{fund.strategy}</p>
            )}
            <DescriptionList rows={keyRows} columns={2} />
            {showCapitalBar && (
              <div className="fund-capital" style={{ marginTop: 'var(--sp-5)' }}>
                <div className="fund-capital-bar">
                  <div className="fund-capital-fill" style={{ width: `${Math.min(committedPct, 100)}%` }} />
                </div>
                <div className="fund-capital-legend">
                  <span>Committed <b>{money(fund.committedCapital!, { compact: true })}</b></span>
                  <span>Target <b>{money(fund.targetSize!, { compact: true })}</b></span>
                </div>
              </div>
            )}
          </SectionCard>

          {/* ---- tranches ---- */}
          <SectionCard
            title={<>Capital <b>Tranches</b></>}
            eyebrow="Structure"
            right={fund.tranches?.length ? <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{fund.tranches.length}</span> : undefined}
          >
            {fund.tranches && fund.tranches.length > 0 ? (
              <div className="fund-tranches">
                {fund.tranches.map((t) => (
                  <div key={t.id} className="fund-tranche">
                    <span className="fund-tranche-name">{t.name}</span>
                    <span className="row row-gap-3">
                      {t.status && <Badge tone="neutral" variant="soft">{t.status}</Badge>}
                      <span className="fund-tranche-size">{t.size != null ? money(t.size, { compact: true }) : '—'}</span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<Layers size={28} />}
                title="Single-tranche vehicle"
                hint="No separate capital tranches are defined for this fund."
              />
            )}
          </SectionCard>

          {/* ---- properties ---- */}
          <SectionCard
            title={<>Assigned <b>Properties</b></>}
            eyebrow="Portfolio"
            right={<span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{props.length} sites</span>}
            pad={false}
          >
            {props.length > 0 ? (
              <DataTable<Property>
                columns={cols}
                rows={props}
                getRowKey={(p) => p.id}
                onRowClick={(p) => navigate(`/properties/${p.id}`)}
                initialSort={{ key: 'rev', dir: 'desc' }}
                emptyText="No properties assigned."
              />
            ) : (
              <div style={{ padding: 'var(--sp-5)' }}>
                <EmptyState
                  icon={<Building2 size={28} />}
                  title="No properties assigned"
                  hint="Assets allocated to this vehicle will appear here once tagged."
                />
              </div>
            )}
          </SectionCard>
        </div>

        {/* ---------- SIDE ---------- */}
        <div className="fund-side">
          <SectionCard title={<>Portfolio <b>Rollup</b></>} eyebrow="Snapshot">
            <DescriptionList
              columns={1}
              rows={[
                { label: 'Properties', value: <span className="num">{num(props.length)}</span> },
                { label: 'Total Units', value: <span className="num">{totalUnits > 0 ? num(totalUnits) : '—'}</span> },
                { label: 'NRSF', value: <span className="num">{wtSqft > 0 ? num(wtSqft) : '—'}</span> },
                { label: 'Wtd. Occupancy', value: <span className="num">{wtOcc != null ? pct(wtOcc) : '—'}</span> },
                { label: 'In-Place Rev (yr)', value: <span className="num">{annualRev > 0 ? money(annualRev, { compact: true }) : '—'}</span> },
              ]}
            />
          </SectionCard>

          {investor && (
            <AssociationCard title="Investor Account" icon={<Building2 size={14} />}>
              <AssocRow
                primary={<Link to={`/accounts/${investor.id}`}>{investor.name}</Link>}
                secondary={`${investor.type} · ${investor.status}`}
              />
            </AssociationCard>
          )}

          {track && (
            <AssociationCard title="Relationship Track" icon={<GitBranch size={14} />}>
              <AssocRow
                primary={<Link to={`/accounts/${track.accountId}/deals`}>{track.name}</Link>}
                secondary={track.type}
              />
            </AssociationCard>
          )}
        </div>
      </div>
    </>
  )
}
